// pfs-radio-group.ts — self-contained Radio-Gruppe (PFS-619).
//
// Gruppen-Label + Radios in der EIGENEN Shadow. Optionen via el.options (nicht
// slotted). Der Radio-Name ist shadow-lokal ("g") → keine Kollision zwischen
// Instanzen (Radio-Gruppierung ist pro Tree/Shadow gescoped).
//
// API: data-label (Gruppen-Label) · data-required · data-error · data-hint ·
//   data-value (INITIAL). Property el.options = [{value,label}|"str", …] ·
//   el.value (get/set). Re-emittiert change composed.
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { takeOwnProperty } from "../../utils/upgrade-property.js";
import {
  installFieldValue,
  bindSelectionValue,
  readFieldValue,
} from "../../composition/field-value.js";

const STYLES = `
:host { display: block; }
.field { display: block; }
.lbl { display: block; font-size: 0.875rem; font-weight: 500; margin: 0 0 0.3rem; color: var(--pfs-color-on-surface); }
.lbl:empty { display: none; }
.req { color: var(--pfs-color-danger); margin-left: 0.15rem; }
.radios { display: flex; flex-wrap: wrap; gap: 1.2rem; }
.opt { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.9rem; font-weight: 500; color: var(--pfs-color-on-surface); cursor: pointer; }
.opt input { margin: 0; flex: none; accent-color: var(--pfs-color-primary); }
.msg { font-size: 0.8rem; margin: 0.3rem 0 0; }
.msg:empty { display: none; }
.msg.error { color: var(--pfs-color-danger); }
.msg.hint { color: var(--pfs-color-on-surface-muted); }
`;

interface Opt {
  value: string;
  label: string;
}

type RadioOption = string | { value: string | number; label?: string };

interface RadioGroupHost extends HTMLElement {
  options: RadioOption[];
  value: string;
}

const normalize = (o: unknown): Opt => {
  if (typeof o === "string") return { value: o, label: o };
  const r = o as { value?: unknown; label?: unknown };
  const value = r.value == null ? "" : String(r.value);
  return { value, label: r.label == null ? value : String(r.label) };
};

defineComponent({
  tagName: "pfs-radio-group",
  mixins: [withShadow()],
  // .value ab Konstruktion pre-connect-sicher (PFS-650, uniform mit text/select).
  created: installFieldValue,
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const field = document.createElement("div");
    field.className = "field";
    const lblEl = document.createElement("span");
    lblEl.className = "lbl";
    const radios = document.createElement("div");
    radios.className = "radios";
    radios.setAttribute("role", "radiogroup");
    field.append(lblEl, radios);
    root.appendChild(field);
    const msg = document.createElement("p");
    msg.className = "msg";
    root.appendChild(msg);

    let opts: Opt[] = [];
    const self = host as RadioGroupHost;
    const preOptions = takeOwnProperty<RadioOption[]>(host, "options");

    // Puffer (field-value) ist die Wahrheit → checked spiegelt ihn; die
    // Reihenfolge options/value ist damit egal (fixt die alte Replay-Fragilität,
    // die den gecheckten <input> aus dem DOM las).
    const reflectChecked = (): void => {
      const cur = readFieldValue(host);
      for (const input of radios.querySelectorAll<HTMLInputElement>("input")) {
        input.checked = input.value === cur;
      }
    };

    const renderRadios = (): void => {
      radios.textContent = "";
      for (const o of opts) {
        const opt = document.createElement("label");
        opt.className = "opt";
        const input = document.createElement("input");
        input.type = "radio";
        input.name = "g"; // shadow-lokal → keine Instanz-Kollision
        input.value = o.value;
        input.addEventListener("change", () => {
          commit(o.value);
          host.dispatchEvent(new Event("change", { bubbles: true, composed: true }));
        });
        const span = document.createElement("span");
        span.textContent = o.label;
        opt.append(input, span);
        radios.appendChild(opt);
      }
      reflectChecked();
    };

    // Auswahl-Naht: der `.value`-Setter (aus .created) spiegelt via reflectChecked;
    // commit() schreibt bei User-Auswahl den Puffer + spiegelt.
    const commit = bindSelectionValue(host, reflectChecked);

    Object.defineProperty(host, "options", {
      configurable: true,
      get: () => opts,
      set: (v: unknown) => {
        opts = Array.isArray(v) ? v.map(normalize) : [];
        renderRadios();
      },
    });

    if (preOptions !== undefined) self.options = preOptions;

    const paint = (): void => {
      const labelText = host.dataset.label ?? "";
      lblEl.textContent = labelText;
      if (host.dataset.required != null && labelText) {
        const s = document.createElement("span");
        s.className = "req";
        s.textContent = "*";
        lblEl.appendChild(s);
      }
      const err = (host.dataset.error ?? "").trim();
      const hint = (host.dataset.hint ?? "").trim();
      if (err) {
        msg.textContent = err;
        msg.className = "msg error";
      } else if (hint) {
        msg.textContent = hint;
        msg.className = "msg hint";
      } else {
        msg.textContent = "";
        msg.className = "msg";
      }
    };
    paint();

    const obs = new MutationObserver(paint);
    obs.observe(host, {
      attributes: true,
      attributeFilter: ["data-label", "data-required", "data-error", "data-hint"],
    });
    ctx.cleanup.push(() => obs.disconnect());
  },
});
